// adminApi.js
// Central place for every call this admin dashboard makes to the eilan-auth-service backend.
// Change API_BASE_URL to match wherever eilan-auth-service is actually running.

const API_BASE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || "https://deckapi.eilandigital.com";

function getAdminToken() {
  return sessionStorage.getItem("eilan_admin_token");
}

async function request(path, options = {}) {
  const token = getAdminToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    let message = "Something went wrong.";
    try {
      const body = await response.json();
      message = body.message || message;
    } catch {
      // response wasn't JSON - fall back to default message
    }
    throw new Error(message);
  }

  // Some endpoints (PATCH/DELETE) return no body
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

export const adminApi = {
  login: (username, password) =>
    request("/api/auth/admin/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  listUsers: () => request("/api/admin/users"),

  listCompanies: () => request("/api/admin/companies"),

  createUser: (companyName, expiryDays) =>
    request("/api/admin/users", {
      method: "POST",
      body: JSON.stringify({ companyName, expiryDays }),
    }),

  changeExpiry: (userId, deltaDays) =>
    request(`/api/admin/users/${userId}/expiry`, {
      method: "PATCH",
      body: JSON.stringify({ deltaDays }),
    }),

  setExpiryDate: (userId, newExpiryDate) =>
    request(`/api/admin/users/${userId}/expiry-date`, {
      method: "PATCH",
      body: JSON.stringify({ newExpiryDate }),
    }),

  updateUsername: (userId, username) =>
    request(`/api/admin/users/${userId}/username`, {
      method: "PATCH",
      body: JSON.stringify({ username }),
    }),

  // manualPassword is optional - pass null/undefined to auto-generate instead
  resetPassword: (userId, manualPassword) =>
    request(`/api/admin/users/${userId}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ manualPassword: manualPassword || null }),
    }),

  getCurrentPassword: (userId) =>
    request(`/api/admin/users/${userId}/current-password`),

  setActive: (userId, active) =>
    request(`/api/admin/users/${userId}/active`, {
      method: "PATCH",
      body: JSON.stringify({ active }),
    }),

  deleteUser: (userId) =>
    request(`/api/admin/users/${userId}`, { method: "DELETE" }),

  getSessions: (userId) => request(`/api/admin/users/${userId}/sessions`),

  getSectionSummary: (userId) =>
    request(`/api/admin/users/${userId}/section-summary`),

  getInteractionSummary: (userId) =>
    request(`/api/admin/users/${userId}/interaction-summary`),

  getActiveIpCount: (userId) =>
    request(`/api/admin/users/${userId}/active-ip-count`),

  // Admin's own profile
  getMyProfile: () => request("/api/admin/profile"),

  updateMyUsername: (username) =>
    request("/api/admin/profile/username", {
      method: "PATCH",
      body: JSON.stringify({ username }),
    }),

  resetMyPassword: (manualPassword) =>
    request("/api/admin/profile/reset-password", {
      method: "POST",
      body: JSON.stringify({ manualPassword: manualPassword || null }),
    }),
};

export function saveAdminToken(token) {
  sessionStorage.setItem("eilan_admin_token", token);
}

export function clearAdminToken() {
  sessionStorage.removeItem("eilan_admin_token");
}

export function isAdminLoggedIn() {
  return !!getAdminToken();
}